import { useCallback, useEffect, useState } from 'react'
import { useAuth } from '../context/AuthContext.js'
import './KioskDisplayControls.css'

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || 'http://localhost:5000'
// Short, so "Kiosk zeigt …" confirms a press within seconds (the kiosk polls every 5 s).
const REFRESH_MS = 10000

const MODES = [
  { key: 'auto',    label: 'Automatisch' },
  { key: 'standby', label: 'Standby' },
  { key: 'black',   label: 'Schwarz' },
  { key: 'open',    label: 'Geöffnet' },
]
const MODE_LABELS = Object.fromEntries(MODES.map(m => [m.key, m.label]))
const SHOWING_LABELS = { standby: 'Standby', black: 'Schwarz', welcome: 'Startbildschirm' }

const WEEKDAYS = ['So', 'Mo', 'Di', 'Mi', 'Do', 'Fr', 'Sa']
const hhmm = (d) => `${String(d.getHours()).padStart(2, '0')}:${String(d.getMinutes()).padStart(2, '0')}`

// "18:00" today, "Do 08:00" on another day.
function formatUntil(iso) {
  if (!iso) return ''
  const d = new Date(iso)
  const sameDay = d.toDateString() === new Date().toDateString()
  return sameDay ? hhmm(d) : `${WEEKDAYS[d.getDay()]} ${hhmm(d)}`
}

function describeSchedule(schedule) {
  switch (schedule.reason) {
    case 'no_schedule': return 'kein Zeitplan — immer geöffnet'
    case 'open':        return 'Zeitplan: geöffnet'
    case 'break':       return `Mittagspause bis ${schedule.reopensAt}`
    case 'holiday':     return `Feiertag${schedule.holidayName ? `: ${schedule.holidayName}` : ''} — geschlossen`
    case 'before_open': return `Zeitplan: geschlossen, öffnet ${schedule.reopensAt}${schedule.holidayName ? ` (${schedule.holidayName})` : ''}`
    default:            return 'Zeitplan: geschlossen'
  }
}

function describeMode(station) {
  const o = station.override
  if (!o) return `Automatisch · ${describeSchedule(station.schedule)}`
  const until = o.until ? `bis ${formatUntil(o.until)}` : 'bis Zurücksetzen'
  return `${MODE_LABELS[o.mode]} · manuell ${until}${o.setBy ? ` · ${o.setBy}` : ''}`
}

// What the kiosk should be showing now, in the kiosk's own terms.
function expectedShowing(station) {
  const mode = station.override?.mode
  if (mode === 'black')   return 'black'
  if (mode === 'standby') return 'standby'
  if (mode === 'open')    return 'welcome'
  return station.schedule.open ? 'welcome' : 'standby'
}

function KioskStatus({ station }) {
  const k = station.kiosk
  if (!k.reachable) return <span className="kd-kiosk kd-kiosk--warn">Kiosk nicht erreichbar</span>
  if (k.showing === 'checkin') {
    return <span className="kd-kiosk">Kiosk: Check-in läuft — wechselt danach</span>
  }
  const ok = k.showing === expectedShowing(station)
  return (
    <span className={`kd-kiosk${ok ? ' kd-kiosk--ok' : ''}`}>
      Kiosk zeigt: {SHOWING_LABELS[k.showing] || k.showing}{ok ? ' ✓' : ''}
    </span>
  )
}

/**
 * Standby / Schwarz / Geöffnet / Automatisch for the kiosks of the stations a tenant
 * owns. Plans/Standby_Erweiterungen_Plan.md §6.
 *
 * Self-contained like BarrierControls, and sits next to it. Renders nothing unless the
 * tenant has the feature and owns at least one station. Changes the kiosk screen only —
 * never barriers or ramp alerts.
 */
export default function KioskDisplayControls({ tenantId }) {
  const { apiFetch, getToken, account } = useAuth()
  const [stations, setStations] = useState([])
  const [busy, setBusy]         = useState(null)   // stationId while a request runs
  const [messages, setMessages] = useState({})     // stationId -> { kind, text }
  const [durations, setDurations] = useState({})   // stationId -> { duration, untilTime }

  const load = useCallback(async () => {
    if (!tenantId) { setStations([]); return }
    try {
      const res = await apiFetch(`/api/admin/kiosk-display/${tenantId}`)
      if (!res.ok) { setStations([]); return }
      const data = await res.json()
      setStations(data.enabled ? data.stations || [] : [])
    } catch {
      /* transient — the next refresh catches up */
    }
  }, [tenantId, apiFetch])

  useEffect(() => { load() }, [load])
  useEffect(() => {
    const id = setInterval(load, REFRESH_MS)
    return () => clearInterval(id)
  }, [load])

  // Someone else pressed a button for one of this tenant's stations.
  useEffect(() => {
    if (!account || !tenantId) return
    let active = true
    let es = null
    let retry = null

    async function connect() {
      if (!active) return
      let token
      try { token = await getToken() } catch { retry = setTimeout(connect, 5000); return }
      if (!active) return
      es = new EventSource(`${BACKEND_URL}/api/admin/events?token=${token}`)
      es.addEventListener('kiosk-display', (e) => {
        try {
          if (JSON.parse(e.data).tenantId === tenantId) load()
        } catch { /* ignore a malformed event */ }
      })
      es.onerror = () => { es.close(); if (active) retry = setTimeout(connect, 5000) }
    }
    connect()

    return () => {
      active = false
      if (retry) clearTimeout(retry)
      es?.close()
    }
  }, [account, tenantId, getToken, load])

  const showMessage = (stationId, kind, text) => {
    setMessages(prev => ({ ...prev, [stationId]: { kind, text } }))
    setTimeout(() => setMessages(prev => {
      if (prev[stationId]?.text !== text) return prev
      const next = { ...prev }
      delete next[stationId]
      return next
    }), 6000)
  }

  // Without a schedule there is no "next change" — the default is then "until reset".
  const durationFor = (station) => durations[station.id] ?? {
    duration: station.schedule.nextChangeAt ? 'next_change' : 'reset',
    untilTime: '',
  }
  const setDuration = (stationId, patch, station) =>
    setDurations(prev => ({ ...prev, [stationId]: { ...durationFor(station), ...patch } }))

  const run = async (station, mode) => {
    const { duration, untilTime } = durationFor(station)
    if (mode !== 'auto' && duration === 'until_time' && !untilTime) {
      showMessage(station.id, 'error', 'Bitte eine Uhrzeit wählen.')
      return
    }

    setBusy(station.id)
    try {
      const res = await apiFetch(`/api/admin/kiosk-display/station/${station.id}`, {
        method: 'POST',
        body: JSON.stringify({ mode, duration, untilTime: untilTime || null }),
      })
      const data = await res.json().catch(() => ({}))
      if (res.ok) {
        const until = mode === 'auto' ? '' : data.until ? ` bis ${formatUntil(data.until)}` : ' bis Zurücksetzen'
        showMessage(station.id, 'ok', `${MODE_LABELS[mode]}${until}`)
        load()
        // The kiosk picks the change up within 5 s — look again so "Kiosk zeigt"
        // confirms it without waiting for the regular refresh.
        setTimeout(load, 6000)
      } else {
        showMessage(station.id, 'error', data.error || 'Umstellen fehlgeschlagen')
      }
    } catch {
      showMessage(station.id, 'error', 'Server nicht erreichbar')
    } finally {
      setBusy(null)
    }
  }

  if (stations.length === 0) return null

  return (
    <div className="card kd-card">
      <div className="section-title">Terminal-Anzeige</div>
      <div className="kd-list">
        {stations.map(s => {
          const current = s.override?.mode ?? 'auto'
          const { duration, untilTime } = durationFor(s)
          const msg = messages[s.id]
          return (
            <div key={s.id} className={`kd-row${s.override ? ' kd-row--manual' : ''}`}>
              <div className="kd-info">
                <span className="kd-label">{s.name}</span>
                <span className="kd-state">{describeMode(s)}</span>
                <KioskStatus station={s} />
                {msg && <span className={`kd-msg kd-msg--${msg.kind}`}>{msg.text}</span>}
              </div>
              <div className="kd-controls">
                <div className="kd-actions">
                  {MODES.map(m => (
                    <button
                      key={m.key}
                      className={`btn kd-btn ${current === m.key ? 'btn-primary' : 'btn-ghost'}`}
                      disabled={busy === s.id}
                      onClick={() => run(s, m.key)}
                    >
                      {m.label}
                    </button>
                  ))}
                  {busy === s.id && <span className="spinner spinner-sm" />}
                </div>
                <div className="kd-duration">
                  <select
                    className="form-control form-control--sm"
                    value={duration}
                    onChange={e => setDuration(s.id, { duration: e.target.value }, s)}
                  >
                    <option value="next_change" disabled={!s.schedule.nextChangeAt}>
                      {s.schedule.nextChangeAt
                        ? `bis zur nächsten planmäßigen Änderung (${formatUntil(s.schedule.nextChangeAt)})`
                        : 'bis zur nächsten planmäßigen Änderung (kein Zeitplan)'}
                    </option>
                    <option value="until_time">bis Uhrzeit</option>
                    <option value="reset">bis Zurücksetzen</option>
                  </select>
                  {duration === 'until_time' && (
                    <input
                      type="time"
                      className="form-control form-control--sm kd-time"
                      value={untilTime}
                      onChange={e => setDuration(s.id, { untilTime: e.target.value }, s)}
                    />
                  )}
                </div>
              </div>
            </div>
          )
        })}
      </div>
    </div>
  )
}
